import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Radio, Timer, Edit } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db } from '@/lib/firebase';
import { doc, onSnapshot, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useOfflineCountdown } from '@/hooks/useOfflineCountdown';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useArtistLiveStatus } from '@/features/artist-mode/contexts/ArtistLiveStatusContext';
import AddSongsRequiredModal from '@/components/AddSongsRequiredModal';
import { sanitizeSessionDurationDraft, clampSessionDurationMinutes } from '@/lib/liveSessionDuration';

const LiveStatusControls = ({ sidebarCollapsed = false, onTriggerSongWizard, onGoLive, onGoOffline, songs = [], pendingRequestsCount = 0, isTrialMode = false }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const mobile = useMobile();
  const { isLive, sessionEndTime, goLive, goOffline } = useArtistLiveStatus();
  const [sessionId, setSessionId] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [showStartModal, setShowStartModal] = useState(false);
  const [showEditTimerModal, setShowEditTimerModal] = useState(false);
  const [startDurationDraft, setStartDurationDraft] = useState('60');
  const [editTimerMinutes, setEditTimerMinutes] = useState(60);
  const [showAddSongsModal, setShowAddSongsModal] = useState(false);
  const [showPendingWarning, setShowPendingWarning] = useState(false);
  const [hasWarnedThisSession, setHasWarnedThisSession] = useState(false);

  useEffect(() => {
    if (showStartModal) setStartDurationDraft('60');
  }, [showStartModal]);
  
  // Use the offline countdown hook
  const {
    showOfflineCountdown,
    offlineCountdown,
    isInCountdownMode,
    startOfflineCountdown,
    cancelOfflineCountdown,
    endLiveSession: hookEndLiveSession
  } = useOfflineCountdown();



  // Timer countdown effect
  useEffect(() => {
    if (!isLive || !sessionEndTime || showOfflineCountdown) {
      setTimeLeft('');
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const timeRemaining = sessionEndTime.getTime() - now.getTime();
      
      if (timeRemaining <= 0) {
        setTimeLeft('00:00');
        return;
      }
      
      const minutes = Math.floor(timeRemaining / 60000);
      const seconds = Math.floor((timeRemaining % 60000) / 1000);
      const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setTimeLeft(timeString);
      console.log('Timer updated:', timeString);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isLive, sessionEndTime, showOfflineCountdown]);




  const updateSessionTimer = async (newDurationMinutes) => {
    if (!user || !isLive) return;
    try {
      const newEndTime = new Date(Date.now() + newDurationMinutes * 60 * 1000);
      const liveStatusRef = doc(db, "live_status", user.uid);
      await updateDoc(liveStatusRef, {
        session_end_time: newEndTime,
      });
      toast({ title: "Timer Updated", description: "Your session timer has been updated." });
    } catch (error) {
      console.error("Error updating timer:", error);
      toast({ title: "Error", description: "Could not update timer.", variant: "destructive" });
    }
  };

  const tryStartOffline = () => {
    // One-time warning if there are pending requests
    if (!hasWarnedThisSession && pendingRequestsCount > 0) {
      setHasWarnedThisSession(true);
      setShowPendingWarning(true);
      return;
    }
    startOfflineCountdown();
  };

  if (isLive) {
    return (
      <>
        <div className="p-4 border-b border-gray-800 bg-green-900/20">
          {!sidebarCollapsed ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`flex items-center space-x-2 text-green-400 ${isTrialMode ? 'animate-pulse' : ''}`}>
                  <Radio className={`w-4 h-4 ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(34,197,94,0.8)] animate-pulse' : ''}`} />
                  <span className={`font-medium text-sm ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]' : ''}`}>LIVE</span>
                </div>
                <Switch
                  aria-label="Go offline"
                  checked={isLive && !showOfflineCountdown}
                  onCheckedChange={(checked) => {
                    console.log('🔍 LiveStatusControls toggle changed:', checked);
                    if (!checked) {
                      console.log('🔍 Attempting to go offline');
                      tryStartOffline();
                    }
                  }}
                  className={`data-[state=checked]:bg-green-600 transition-all duration-1000 ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] shadow-[0_0_20px_rgba(34,197,94,0.6)] animate-pulse' : ''} ${(!isLive || showOfflineCountdown) ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black animate-pulse' : ''}`}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="px-2 py-1 rounded-md bg-green-900/40 border border-green-700/40 text-green-300 font-mono text-xs flex items-center">
                  <Timer className="w-3 h-3 mr-1" /> {timeLeft || '—:—'}
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-6 px-2 border-gray-600 text-gray-200 hover:bg-gray-700 text-xs" 
                  onClick={() => {
                    const now = new Date();
                    const remainingMs = (sessionEndTime ? sessionEndTime.getTime() : now.getTime()) - now.getTime();
                    const mins = Math.max(1, Math.ceil(remainingMs / 60000));
                    setEditTimerMinutes(mins);
                    setShowEditTimerModal(true);
                  }}
                >
                  <Edit className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <div className={`w-6 h-6 bg-green-600 rounded-full flex items-center justify-center ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] animate-pulse shadow-[0_0_20px_rgba(34,197,94,0.6)]' : ''}`}>
                <Radio className={`w-3 h-3 text-white ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]' : ''}`} />
              </div>
              <Switch
                aria-label="Go offline"
                checked={isLive && !showOfflineCountdown}
                onCheckedChange={(checked) => {
                  console.log('🔍 LiveStatusControls collapsed toggle changed:', checked);
                  if (!checked) {
                    console.log('🔍 Attempting to go offline (collapsed)');
                    tryStartOffline();
                  }
                }}
                className={`data-[state=checked]:bg-green-600 transition-all duration-1000 ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] shadow-[0_0_20px_rgba(34,197,94,0.6)] animate-pulse' : ''} ${(!isLive || showOfflineCountdown) ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black animate-pulse' : ''}`}
              />
              <div className="text-green-300 font-mono text-xs text-center">
                {timeLeft || '—:—'}
              </div>
            </div>
          )}
        </div>

        {/* Edit Timer Modal */}
        <Dialog open={showEditTimerModal} onOpenChange={setShowEditTimerModal}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white">
            <DialogHeader>
              <DialogTitle>Update Session Timer</DialogTitle>
              <DialogDescription>
                Set a new duration for your live session.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="editDuration" className="text-gray-300">New Duration (minutes)</Label>
                <Input
                  id="editDuration"
                  type="number"
                  min="1"
                  max="480"
                  value={editTimerMinutes}
                  onChange={(e) => setEditTimerMinutes(parseInt(e.target.value) || 60)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditTimerModal(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                updateSessionTimer(editTimerMinutes);
                setShowEditTimerModal(false);
              }}>
                Update Timer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {/* Start Live Session Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Start Live Session</DialogTitle>
            <DialogDescription>
              Set the duration for your live session. You can extend it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="duration" className="text-gray-300">Session Duration (minutes)</Label>
              <Input
                id="duration"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={startDurationDraft}
                onChange={(e) => setStartDurationDraft(sanitizeSessionDurationDraft(e.target.value))}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // Allow immediate Go Live if songs are present OR the first-time flag is set
              const songsAddedFlag = localStorage.getItem('ysr_songs_added_first_time') === 'true';
              const hasSongsInState = Array.isArray(songs) && songs.length > 0;
              console.log('🔍 DEBUG: Go Live gating:', { hasSongsInState, songsAddedFlag, songsLength: songs?.length });

              if (!hasSongsInState && !songsAddedFlag) {
                console.log('🚫 No songs detected and flag not set → show Add Songs modal');
                setShowAddSongsModal(true);
                setShowStartModal(false);
                return;
              }

              (async () => {
                try {
                  const mins = clampSessionDurationMinutes(startDurationDraft);
                  let result;
                  if (onGoLive) {
                    result = await onGoLive(mins);
                  } else {
                    result = await goLive('', user?.uid, mins);
                  }
                  
                  setShowStartModal(false);
                } catch (error) {
                  console.error('Error going live:', error);
                  toast({
                    title: "Error",
                    description: "Failed to go live. Please try again.",
                    variant: "destructive"
                  });
                }
              })()
            }}>
              Go Live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time Pending Requests Warning Modal */}
      <Dialog open={showPendingWarning} onOpenChange={setShowPendingWarning}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Pending Requests</DialogTitle>
            <DialogDescription>
              There {pendingRequestsCount === 1 ? 'is' : 'are'} {pendingRequestsCount} request{pendingRequestsCount === 1 ? '' : 's'} left unfulfilled.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-300">
            If you go offline now, these requests won't be performed.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingWarning(false)}>
              Go Back and Perform Requests
            </Button>
            <Button onClick={() => { setShowPendingWarning(false); startOfflineCountdown(); }}>
              Go Offline Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Songs Required Modal */}
      <AddSongsRequiredModal
        isOpen={showAddSongsModal}
        onClose={() => setShowAddSongsModal(false)}
        onNavigateToCatalogue={() => {
          setShowAddSongsModal(false);
          if (onTriggerSongWizard) {
            onTriggerSongWizard();
          } else {
            localStorage.setItem('ysr_show_song_wizard', 'true');
          }
        }}
      />
    </>
  );
};

export default LiveStatusControls;
